import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useMemo, useRef } from "react";
import { appSounds } from "@/lib/appSounds";

interface SplashScreenProps {
  onComplete?: () => void;
  minDuration?: number;
}

export const SplashScreen = ({ onComplete, minDuration = 2500 }: SplashScreenProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const soundPlayed = useRef(false);

  // Detect mobile and motion preferences once on mount
  useEffect(() => {
    setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  // Play splash sound once
  useEffect(() => {
    if (!soundPlayed.current && !prefersReducedMotion) {
      // Small delay to ensure user interaction context
      const timer = setTimeout(() => {
        appSounds.playSplashSound();
        soundPlayed.current = true;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [prefersReducedMotion]);

  // Shorter duration for reduced motion preference
  const actualDuration = prefersReducedMotion ? 1000 : minDuration;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, actualDuration);

    return () => clearTimeout(timer);
  }, [actualDuration, onComplete]);

  // Fewer stars for mobile/reduced motion
  const starCount = prefersReducedMotion ? 0 : isMobile ? 8 : 16;
  
  // Memoize stars positions
  const stars = useMemo(() => 
    [...Array(starCount)].map((_, i) => ({
      id: i,
      left: `${(i * 37 + 10) % 90}%`,
      top: `${(i * 23 + 5) % 50}%`,
      size: i % 3 === 0 ? 2 : 1,
      delay: i * 0.1,
    })), [starCount]
  );

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0.1 : 0.4 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
          style={{ 
            background: "linear-gradient(180deg, #041f1a 0%, #0a2f28 50%, #062420 100%)"
          }}
        >
          {/* Aurora background glow */}
          {!prefersReducedMotion && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 0.5, scale: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="absolute top-0 left-1/4 w-[500px] h-[400px] rounded-full"
                style={{ 
                  background: "radial-gradient(ellipse, hsl(160 84% 45% / 0.5), transparent 70%)",
                  filter: "blur(80px)"
                }}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 0.4, scale: 1 }}
                transition={{ duration: 1.5, delay: 0.3, ease: "easeOut" }}
                className="absolute top-1/4 right-1/4 w-[400px] h-[300px] rounded-full"
                style={{ 
                  background: "radial-gradient(ellipse, hsl(168 76% 50% / 0.4), transparent 70%)",
                  filter: "blur(80px)"
                }}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 0.3, scale: 1 }}
                transition={{ duration: 1.5, delay: 0.6, ease: "easeOut" }}
                className="absolute bottom-1/4 left-1/3 w-[350px] h-[250px] rounded-full"
                style={{ 
                  background: "radial-gradient(ellipse, hsl(172 66% 55% / 0.35), transparent 70%)",
                  filter: "blur(80px)"
                }}
              />
            </div>
          )}

          {/* Animated stars */}
          {starCount > 0 && (
            <div className="absolute inset-0 pointer-events-none">
              {stars.map((star) => (
                <motion.div
                  key={star.id}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: [0, 1, 0.6, 1], scale: 1 }}
                  transition={{ 
                    duration: 2, 
                    delay: star.delay,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                  className="absolute bg-emerald-200/80 rounded-full"
                  style={{ 
                    left: star.left, 
                    top: star.top,
                    width: star.size,
                    height: star.size,
                  }}
                />
              ))}
            </div>
          )}

          {/* Logo and content */}
          <div className="relative flex flex-col items-center">
            {/* Logo with glow effect */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                ease: [0.16, 1, 0.3, 1],
                delay: 0.2
              }}
              className="relative"
            >
              {/* Glow ring */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ 
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1, 1.1, 1],
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute -inset-4 rounded-[32px]"
                style={{
                  background: "radial-gradient(circle, hsl(160 84% 50% / 0.4), transparent 70%)",
                  filter: "blur(20px)"
                }}
              />
              
              {/* Logo image */}
              <img 
                src="/favicon.png" 
                alt="Новкъаст GO" 
                className="w-28 h-28 rounded-[28px] shadow-2xl relative z-10"
                style={{
                  boxShadow: "0 0 40px hsl(160 84% 50% / 0.3), 0 20px 40px rgba(0,0,0,0.3)"
                }}
              />
            </motion.div>

            {/* App name with stagger animation */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-8 text-center"
            >
              <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-300 via-teal-400 to-green-400 bg-clip-text text-transparent">
                Новкъаст GO
              </h1>
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="text-sm text-emerald-200/70 mt-3 tracking-wide"
              >
                Такси • Еда • Доставка
              </motion.p>
            </motion.div>

            {/* Loading indicator */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-10 flex gap-2"
            >
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{ 
                    duration: 0.8,
                    delay: i * 0.15,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-emerald-400 to-teal-400"
                />
              ))}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
